import React, { useEffect, useMemo, useRef, useState } from 'react'
import trainService, { TrainProjectNode, TrainRunDetail, TrainRunSummary } from '../services/trainService'
import releaseService, { ReleaseDetailResponse, ReleaseMetaJson, ReleaseResponse } from '../services/releaseService'

interface Props {
  userInfo: { email: string; fullName: string; isAdmin: boolean; permissions: string[] }
}

type Node = TrainProjectNode

function normalizeIsoToDate(input: string): Date | null {
  const s = String(input || '').trim()
  if (!s) return null

  // timezone 정보가 없으면(서버가 UTC를 naive로 내려주는 케이스) UTC로 간주한다.
  const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(s)
  let iso = hasTimezone ? s : `${s}Z`

  // JS Date 파서가 microseconds(6자리) 를 싫어하는 브라우저가 있어 ms(3자리)로 축약
  iso = iso.replace(/(\.\d{3})\d+(Z|[+-]\d{2}:\d{2})$/, '$1$2')
  iso = iso.replace(/(\.\d{3})\d+$/, '$1')

  // 시간대 표시가 없으면 UTC 로 본다 (서버가 naive UTC 를 내린다). 안 붙이면 9시간 어긋난다.
  const hasTZ = /[zZ]|([+-]\d{2}:?\d{2})$/.test(iso)
  const d = new Date(hasTZ ? iso : (iso.includes('T') ? `${iso}Z` : `${iso.replace(' ', 'T')}Z`))
  if (Number.isNaN(d.getTime())) return null
  return d
}

function formatDT(s?: string | null) {
  if (!s) return '-'
  const d = normalizeIsoToDate(s)
  if (!d) return s
  try {
    return d.toLocaleString('ko-KR', { hour12: true })
  } catch {
    return d.toLocaleString()
  }
}

type ToastVariant = 'info' | 'success' | 'error'
type ToastState = { id: number; message: string; variant: ToastVariant } | null

function prettyJson(v: any) {
  try { return JSON.stringify(v, null, 2) } catch { return String(v) }
}

function formatBytes(n?: number | null) {
  const v = Number(n)
  if (!Number.isFinite(v) || v <= 0) return '-'
  if (v < 1024) return `${v.toLocaleString()} B`
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`
  if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)} MB`
  return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function formatDuration(start?: string | null, end?: string | null) {
  if (!start || !end) return null
  const s = normalizeIsoToDate(start)
  const e = normalizeIsoToDate(end)
  if (!s || !e) return null
  const sec = Math.floor((e.getTime() - s.getTime()) / 1000)
  if (sec < 0) return null
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const ss = sec % 60
  if (h > 0) return `${h}시간 ${m}분`
  if (m > 0) return `${m}분 ${ss}초`
  return `${ss}초`
}

// meta.json 값 표시용: 숫자는 소수 4자리로 정리, 객체는 한 줄 JSON
function formatMetaValue(v: any): string {
  if (v === null || v === undefined || v === '') return '-'
  if (typeof v === 'number') return Number.isInteger(v) ? v.toLocaleString() : String(Number(v.toFixed(4)))
  if (typeof v === 'object') { try { return JSON.stringify(v) } catch { return String(v) } }
  return String(v)
}

function personLabel(p?: { name?: string; email?: string } | null) {
  const name = String(p?.name || '').trim()
  const email = String(p?.email || '').trim()
  if (name && email) return `${name} (${email})`
  return name || email || '-'
}

function DetailSection({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="border rounded">
      <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-gray-800">{title}</div>
        {subtitle && <div className="text-xs text-gray-500 text-right">{subtitle}</div>}
      </div>
      <div className="p-3 text-sm text-gray-700">{children}</div>
    </div>
  )
}

function DetailField({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm text-gray-900 break-words">{value ?? '-'}</div>
    </div>
  )
}

function KeyValueGrid({ map }: { map?: Record<string, any> | null }) {
  const keys = Object.keys(map || {})
  if (keys.length === 0) return <div className="text-sm text-gray-500">-</div>
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {keys.map(k => (
        <DetailField key={k} label={k} value={formatMetaValue((map as any)[k])} />
      ))}
    </div>
  )
}

function XButton({
  onClick,
  ariaLabel,
  disabled,
}: {
  onClick: () => void
  ariaLabel: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      className={`p-2 rounded text-gray-600 hover:text-gray-900 ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
      disabled={disabled}
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 6l12 12M18 6L6 18" />
      </svg>
    </button>
  )
}

function Toast({ toast, onClose }: { toast: ToastState; onClose: () => void }) {
  if (!toast) return null
  const style =
    toast.variant === 'success'
      ? 'bg-green-50 border-green-200 text-green-900'
      : toast.variant === 'error'
        ? 'bg-red-50 border-red-200 text-red-900'
        : 'bg-gray-50 border-gray-200 text-gray-900'
  return (
    <div className="fixed top-4 right-4 z-[60] w-[min(520px,92vw)]">
      <div className={`border rounded-lg shadow-md px-3 py-2 flex items-start gap-2 ${style}`}>
        <div className="text-sm leading-relaxed whitespace-pre-wrap break-words flex-1">{toast.message}</div>
        <XButton onClick={onClose} ariaLabel="알림 닫기" />
      </div>
    </div>
  )
}

export default function ModelReleaseManagement({ userInfo: _userInfo }: Props) {
  const [items, setItems] = useState<ReleaseResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [detail, setDetail] = useState<ReleaseDetailResponse | null>(null)
  const [toast, setToast] = useState<ToastState>(null)
  const [retrying, setRetrying] = useState(false)
  const reloadSeqRef = useRef(0)
  const detailRef = useRef<ReleaseDetailResponse | null>(null)

  const [showCreate, setShowCreate] = useState(false)

  const PAGE_SIZE = 50

  const pushToast = (message: string, variant: ToastVariant = 'info') => {
    setToast({ id: Date.now(), message, variant })
  }

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 5500)
    return () => clearTimeout(t)
  }, [toast?.id])

  useEffect(() => {
    detailRef.current = detail
  }, [detail])

  const mergeById = (primary: ReleaseResponse[], secondary: ReleaseResponse[]) => {
    const seen = new Set<number>()
    const out: ReleaseResponse[] = []
    for (const it of primary) {
      if (!seen.has(it.id)) { seen.add(it.id); out.push(it) }
    }
    for (const it of secondary) {
      if (!seen.has(it.id)) { seen.add(it.id); out.push(it) }
    }
    return out
  }

  const reload = async (opts?: { silent?: boolean; keepExisting?: boolean }) => {
    const silent = Boolean(opts?.silent)
    const seq = ++reloadSeqRef.current
    if (!silent) setLoading(true)
    try {
      const next = await releaseService.list({ page: 1, size: PAGE_SIZE })
      if (seq !== reloadSeqRef.current) return

      // 첫 페이지는 항상 최신으로 교체하고, keepExisting이면 뒤(이전 페이지들)는 유지(중복 제거)
      setItems(prev => (opts?.keepExisting ? mergeById(next, prev) : next))
      setPage(1)
      setHasMore(next.length === PAGE_SIZE)
    } catch (e: any) {
      // 폴링/새로고침 도중 실패해도 목록을 비우지 않는다(건수 왔다갔다 방지)
      if (!silent) pushToast(String(e?.message || e), 'error')
    } finally {
      if (!silent) setLoading(false)
    }
  }

  useEffect(() => {
    reload()
  }, [])

  const refreshDetail = async (releaseId: number) => {
    try {
      const d = await releaseService.get(releaseId)
      // 모달이 닫혔거나 다른 id로 바뀌었으면 무시
      if (detailRef.current && detailRef.current.id === releaseId) {
        setDetail(d)
      }
    } catch {
      // 폴링 중 실패는 무시 (리스트는 계속 갱신됨)
    }
  }

  // 상세 모달이 열려있고 PENDING이면 자동으로 상세를 갱신한다.
  useEffect(() => {
    if (!detail?.id) return
    let cancelled = false
    let inFlight = false

    const tick = async () => {
      if (cancelled || inFlight) return
      const cur = detailRef.current
      if (!cur || cur.id !== detail.id) return
      if (String(cur.status || '').toUpperCase() !== 'PENDING') return
      inFlight = true
      try {
        await refreshDetail(cur.id)
      } finally {
        inFlight = false
      }
    }

    // 열자마자 1회 갱신 (create 응답이 PENDING일 수 있음)
    void refreshDetail(detail.id)

    const id = window.setInterval(() => { void tick() }, 3000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [detail?.id])

  // 리스트에서 상태가 바뀌면(예: UPLOADED) 상세도 한 번 더 갱신한다.
  useEffect(() => {
    if (!detail?.id) return
    const row = items.find(it => it.id === detail.id)
    if (!row) return
    if (String(row.status || '').toUpperCase() !== String(detail.status || '').toUpperCase()) {
      void refreshDetail(detail.id)
    }
  }, [items, detail?.id])

  const hasPending = useMemo(() => items.some(it => String(it.status || '').toUpperCase() === 'PENDING'), [items])
  useEffect(() => {
    if (!hasPending) return
    let cancelled = false
    const id = window.setInterval(async () => {
      if (cancelled) return
      // PENDING이 있는 동안만 주기적으로 갱신
      await reload({ silent: true, keepExisting: true })
    }, 5000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [hasPending])

  return (
    <div className="p-6">
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-2xl font-bold text-gray-900">모델 배포 업로드</div>
          <div className="text-sm text-gray-600 mt-1">
            학습이력(Run)에서 모델 아티팩트(.pt/.onnx/.hef) 1개를 선택해 업로드하여 S3에 저장하고, 배포이력을 저장합니다.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800"
            onClick={() => setShowCreate(true)}
          >
            업로드 실행
          </button>
          {hasPending ? (
            <span className="px-3 py-2 rounded border border-amber-200 bg-amber-50 text-amber-900 text-sm">
              자동 갱신 중(5초)
            </span>
          ) : (
            <button
              className="px-3 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              onClick={() => reload()}
              disabled={loading}
            >
              새로고침
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="font-semibold text-gray-900">배포 이력</div>
          <div className="text-sm text-gray-500">{loading ? '로딩 중…' : `${items.length}건`}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left px-4 py-2">모델 파일</th>
                <th className="text-center px-4 py-2">생성</th>
                <th className="text-center px-4 py-2">업로드</th>
                <th className="text-center px-4 py-2">상태</th>
                <th className="text-center px-4 py-2">비고</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && items.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-center text-gray-500" colSpan={5}>
                    불러오는 중…
                  </td>
                </tr>
              )}
              {items.map(it => (
                <tr
                  key={it.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={async () => {
                    try {
                      const d = await releaseService.get(it.id)
                      setDetail(d)
                    } catch (e: any) {
                      setDetail(null)
                      pushToast(String(e?.message || e), 'error')
                    }
                  }}
                  title="클릭하면 상세가 열립니다"
                >
                  <td className="px-4 py-2 text-gray-900">{it.model_file_name}</td>
                  <td className="px-4 py-2 text-gray-600 text-center">{formatDT(it.created_at)}</td>
                  <td className="px-4 py-2 text-gray-600 text-center">
                    {String(it.status || '').toUpperCase() === 'PENDING' ? '업로드 중…' : formatDT(it.uploaded_at)}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${
                      it.status === 'UPLOADED' ? 'bg-green-100 text-green-800'
                        : it.status === 'FAILED' ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}>
                      {it.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-600 text-center max-w-[520px]">
                    {String(it.status || '').toUpperCase() === 'FAILED' ? (
                      <span className="block truncate" title={it.error_message || '업로드 실패'}>
                        {it.error_message || '업로드 실패'}
                      </span>
                    ) : String(it.status || '').toUpperCase() === 'PENDING' ? (
                      '처리 중'
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-center text-gray-500" colSpan={5}>
                    아직 배포 이력이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {(hasMore || loadingMore) && (
          <div className="px-4 py-3 border-t bg-white flex items-center justify-center">
            <button
              className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              disabled={loadingMore}
              onClick={async () => {
                if (loadingMore) return
                setLoadingMore(true)
                try {
                  const nextPage = page + 1
                  const list = await releaseService.list({ page: nextPage, size: PAGE_SIZE })
                  setItems(prev => mergeById(prev, list))
                  setPage(nextPage)
                  setHasMore(list.length === PAGE_SIZE)
                } catch (e: any) {
                  pushToast(String(e?.message || e), 'error')
                } finally {
                  setLoadingMore(false)
                }
              }}
            >
              {loadingMore ? '불러오는 중…' : `더보기 (${PAGE_SIZE})`}
            </button>
          </div>
        )}
      </div>

      {detail && (
        <ReleaseDetailModal
          item={detail}
          onClose={() => setDetail(null)}
          retrying={retrying}
          onRetry={async () => {
            if (retrying) return
            try {
              setRetrying(true)
              const next = await releaseService.retry(detail.id)
              await reload()
              setDetail(next)
              pushToast('재시도 요청이 완료되었습니다.', 'success')
            } catch (e: any) {
              pushToast(String(e?.message || e), 'error')
            } finally {
              setRetrying(false)
            }
          }}
        />
      )}

      <CreateReleaseModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={async (created) => {
          await reload()
          setDetail(created)
        }}
      />
    </div>
  )
}

function ReleaseDetailModal({
  item,
  onClose,
  onRetry,
  retrying,
}: {
  item: ReleaseDetailResponse
  onClose: () => void
  onRetry: () => void
  retrying: boolean
}) {
  const prevOverflowRef = useRef<string>('')
  useEffect(() => {
    prevOverflowRef.current = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prevOverflowRef.current }
  }, [])

  const meta: ReleaseMetaJson = item.meta_json || {}
  const modelFile = meta.model_file
  const training = meta.training
  const dataset = meta.dataset
  const generated = meta.generated
  const metrics = training?.metrics || {}
  const hasMetrics = Object.keys(metrics).length > 0
  const requiredInputs = training?.inputs?.required
  const extraInputs = training?.inputs?.extras
  const hasInputs = Object.keys(requiredInputs || {}).length > 0 || Object.keys(extraInputs || {}).length > 0
  const envInfo = training?.training_env_info || {}
  const hasEnvInfo = Object.keys(envInfo).length > 0
  const trainDuration = formatDuration(training?.started_at, training?.finished_at)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white w-[min(920px,95vw)] max-h-[90vh] rounded-lg shadow-lg overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="font-semibold text-gray-900">배포 상세 (#{item.id})</div>
          <div className="flex items-center gap-2">
            {item.status === 'FAILED' && (
              <button
                type="button"
                className="px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                disabled={retrying}
                onClick={onRetry}
              >
                {retrying ? '재시도 중…' : '재시도'}
              </button>
            )}
            <XButton onClick={onClose} ariaLabel="닫기" />
          </div>
        </div>
        <div className="p-4 space-y-3 overflow-auto max-h-[calc(90vh-56px)]">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="p-3 border rounded">
              <div className="text-gray-500">상태</div>
              <div className="mt-1">
                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${
                  item.status === 'UPLOADED' ? 'bg-green-100 text-green-800'
                    : item.status === 'FAILED' ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                }`}>
                  {item.status}
                </span>
              </div>
              {item.error_message && <div className="text-red-700 mt-2 whitespace-pre-wrap">{item.error_message}</div>}
            </div>
            <div className="p-3 border rounded">
              <div className="text-gray-500">배포 정보</div>
              <div className="text-gray-900 mt-1">배포자: {personLabel(generated?.generated_by)}</div>
              <div className="text-gray-600 mt-1">요청: {formatDT(item.created_at)}</div>
              <div className="text-gray-600">meta 생성: {formatDT(generated?.generated_at)}</div>
              <div className="text-gray-600">업로드 완료: {formatDT(item.uploaded_at)}</div>
            </div>
          </div>

          <DetailSection title="모델 파일">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <DetailField label="파일명" value={modelFile?.name || item.model_file_name} />
              <DetailField
                label="포맷 / 크기"
                value={`${(modelFile?.format || '').toUpperCase() || '-'} · ${formatBytes(modelFile?.size_bytes)}`}
              />
              <DetailField
                label="S3 버킷"
                value={item.bucket ? `${item.bucket}${item.region ? ` (${item.region})` : ''}` : '-'}
              />
              <DetailField
                label="S3 경로"
                value={item.model_key ? (
                  <span className="inline-flex items-start gap-2">
                    <span className="font-mono text-xs break-all">{item.model_key}</span>
                    <button
                      className="shrink-0 px-2 py-0.5 rounded border border-gray-300 text-xs text-gray-700 hover:bg-gray-50"
                      onClick={async () => { await navigator.clipboard.writeText(item.model_key) }}
                    >
                      복사
                    </button>
                  </span>
                ) : '-'}
              />
            </div>
          </DetailSection>

          <DetailSection title="학습 정보" subtitle="학습 이력 관리의 Run 기록">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <DetailField
                label="프로젝트 / 작업 카테고리"
                value={[training?.project_name, training?.task_name].filter(Boolean).join(' / ') || '-'}
              />
              <DetailField
                label="학습 Run"
                value={`#${item.train_run_id}${training?.status ? ` · ${training.status}` : ''}`}
              />
              <DetailField label="학습 실행자" value={personLabel(training?.created_by)} />
              <DetailField label="Run 생성" value={formatDT(training?.created_at)} />
              <DetailField
                label="학습 기간"
                value={training?.started_at || training?.finished_at
                  ? `${formatDT(training?.started_at)} ~ ${formatDT(training?.finished_at)}${trainDuration ? ` (${trainDuration})` : ''}`
                  : '-'}
              />
              {training?.description && <DetailField label="비고" value={training.description} />}
            </div>
            {hasMetrics && (
              <div className="mt-3 pt-3 border-t">
                <div className="mb-2">
                  <span className="text-sm font-medium text-gray-800">메트릭</span>
                  <span className="ml-2 text-xs text-gray-500">학습 완료 시 자동 기록된 결과</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.keys(metrics).map(k => (
                    <DetailField key={k} label={k} value={formatMetaValue(metrics[k])} />
                  ))}
                </div>
              </div>
            )}
            {hasInputs && (
              <details className="mt-3 pt-3 border-t">
                <summary className="text-sm text-gray-700 cursor-pointer select-none hover:text-gray-900">
                  학습 입력 파라미터 보기
                  <span className="ml-2 text-xs text-gray-500">학습 실행 시 사용자가 입력한 값</span>
                </summary>
                <div className="mt-2 space-y-3">
                  {requiredInputs && Object.keys(requiredInputs).length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1">필수 입력</div>
                      <KeyValueGrid map={requiredInputs} />
                    </div>
                  )}
                  {extraInputs && Object.keys(extraInputs).length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1">기타 입력</div>
                      <KeyValueGrid map={extraInputs} />
                    </div>
                  )}
                </div>
              </details>
            )}
            {hasEnvInfo && (
              <details className="mt-3 pt-3 border-t">
                <summary className="text-sm text-gray-700 cursor-pointer select-none hover:text-gray-900">
                  학습 환경 정보 보기
                  <span className="ml-2 text-xs text-gray-500">학습 시 자동 수집</span>
                </summary>
                <div className="mt-2">
                  <KeyValueGrid map={envInfo} />
                </div>
              </details>
            )}
          </DetailSection>

          {dataset && (
            <DetailSection title="데이터셋" subtitle="학습 데이터셋 관리에서 생성">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <DetailField label="이름" value={dataset.name || '-'} />
                {dataset.label_version_name && <DetailField label="라벨 버전" value={dataset.label_version_name} />}
                {dataset.label_format && <DetailField label="라벨 포맷" value={dataset.label_format} />}
                {dataset.output?.task && <DetailField label="출력 태스크" value={dataset.output.task} />}
                {dataset.output?.format && <DetailField label="출력 포맷" value={dataset.output.format} />}
                {dataset.output?.created_by && (
                  <DetailField label="데이터셋 생성자" value={personLabel(dataset.output.created_by)} />
                )}
              </div>
              {dataset.split && (
                <div className="mt-3 pt-3 border-t grid grid-cols-2 md:grid-cols-4 gap-3">
                  <DetailField label="전체" value={formatMetaValue(dataset.split.total)} />
                  <DetailField label="Train" value={formatMetaValue(dataset.split.train)} />
                  <DetailField label="Val" value={formatMetaValue(dataset.split.val)} />
                  <DetailField label="Test" value={formatMetaValue(dataset.split.test)} />
                </div>
              )}
              {dataset.sampling && (
                <details className="mt-3 pt-3 border-t">
                  <summary className="text-sm text-gray-700 cursor-pointer select-none hover:text-gray-900">
                    샘플링 설정 보기
                  </summary>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <DetailField label="method" value={formatMetaValue(dataset.sampling.method)} />
                    <DetailField label="seed" value={formatMetaValue(dataset.sampling.seed)} />
                    <DetailField label="max_per_class" value={formatMetaValue(dataset.sampling.max_per_class)} />
                    <DetailField label="splits" value={formatMetaValue(dataset.sampling.splits)} />
                  </div>
                </details>
              )}
            </DetailSection>
          )}

          <details className="border rounded">
            <summary className="px-3 py-2 bg-gray-50 text-sm font-medium text-gray-800 cursor-pointer select-none hover:bg-gray-100 rounded">
              meta.json 원본
              <span className="ml-2 text-xs font-normal text-gray-500">클릭하여 펼치기/접기</span>
            </summary>
            <div className="p-3 border-t">
              <div className="flex justify-end mb-2">
                <button
                  className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                  onClick={async () => {
                    await navigator.clipboard.writeText(prettyJson(item.meta_json || {}))
                  }}
                >
                  복사
                </button>
              </div>
              <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-[420px]">
                {prettyJson(item.meta_json || {})}
              </pre>
            </div>
          </details>
        </div>
      </div>
    </div>
  )
}

function CreateReleaseModal({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean
  onClose: () => void
  onCreated: (created: ReleaseDetailResponse) => void | Promise<void>
}) {
  const [nodes, setNodes] = useState<Node[]>([])
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null)
  const [runs, setRuns] = useState<TrainRunSummary[]>([])
  const [loadingRuns, setLoadingRuns] = useState<boolean>(false)
  const [selectedRun, setSelectedRun] = useState<TrainRunDetail | null>(null)
  const [selectedArtifactId, setSelectedArtifactId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  const prevOverflowRef = useRef<string>('')
  useEffect(() => {
    if (!isOpen) return
    prevOverflowRef.current = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prevOverflowRef.current }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    ;(async () => {
      setError(null)
      setConfirming(false)
      setSelectedTaskId(null); setRuns([]); setSelectedRun(null); setSelectedArtifactId(null)
      try { setNodes(await trainService.listProjects()) } catch { setNodes([]) }
    })()
  }, [isOpen])

  const roots = useMemo(() => (
    nodes
      .filter(n => n.kind === 'project' && n.parent_id == null)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
  ), [nodes])

  // 노드 중복 제거(안정성)
  useEffect(() => {
    if (!nodes || nodes.length === 0) return
    const map = new Map<string, Node>()
    let changed = false
    for (const n of nodes) {
      const key = `${n.kind}-${n.id}`
      if (!map.has(key)) map.set(key, n)
      else changed = true
    }
    if (changed) setNodes(Array.from(map.values()))
  }, [nodes])

  const childrenOf = (parent: Node) => {
    const expectedChildKind = parent.kind === 'project' ? 'task' : null
    if (!expectedChildKind) return [] as Node[]
    return nodes
      .filter(n => n.kind === expectedChildKind && n.parent_id === parent.id)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
  }

  const loadRuns = async (taskId: number) => {
    setLoadingRuns(true); setSelectedRun(null); setSelectedArtifactId(null)
    try { setRuns(await trainService.listRuns(taskId, { sort: 'created_at:desc', size: 50 })) } catch { setRuns([]) } finally { setLoadingRuns(false) }
  }

  const renderTree = (node: Node, level = 0, visited: Set<string> = new Set()) => {
    const isTask = node.kind === 'task'
    const key = `${node.kind}-${node.id}`
    const isExpanded = expanded.has(key)
    const childList = childrenOf(node)
    const isSelected = isTask && selectedTaskId === node.id
    const visitKey = `${node.kind}-${node.id}`
    if (visited.has(visitKey)) return null
    const nextVisited = new Set(visited)
    nextVisited.add(visitKey)
    return (
      <div key={`${node.kind}-${node.id}`}>
        <div
          className={`flex items-center py-2 px-2 cursor-pointer rounded transition-colors ${isSelected ? 'bg-gray-100 border-l-4 border-gray-800 text-gray-900' : 'hover:bg-gray-100'}`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={() => {
            if (node.kind === 'task') {
              setSelectedTaskId(node.id)
              loadRuns(node.id)
            } else if (childList.length > 0) {
              setExpanded(prev => { const ns = new Set(prev); ns.has(key) ? ns.delete(key) : ns.add(key); return ns })
            }
          }}
        >
          {childList.length > 0 && (
            <button
              className="mr-1 w-4 h-4 flex items-center justify-center"
              onClick={(e) => { e.stopPropagation(); setExpanded(prev => { const ns = new Set(prev); ns.has(key) ? ns.delete(key) : ns.add(key); return ns }) }}
            >
              <svg className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
            </button>
          )}
          <svg className="w-5 h-5 text-gray-700 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <span className="text-sm font-medium text-gray-900">{node.name}</span>
        </div>
        {childList.length > 0 && isExpanded && (
          <div>
            {childList.map(c => renderTree(c, level + 1, nextVisited))}
          </div>
        )}
      </div>
    )
  }

  const filteredArtifacts = useMemo(() => {
    if (!selectedRun) return []
    return (selectedRun.artifacts || []).filter(a => {
      const n = String(a.file_name || '').toLowerCase()
      return n.endsWith('.pt') || n.endsWith('.onnx') || n.endsWith('.hef')
    })
  }, [selectedRun])

  const selectedArtifact = useMemo(
    () => filteredArtifacts.find(a => a.id === selectedArtifactId) || null,
    [filteredArtifacts, selectedArtifactId]
  )

  const selectedTaskName = useMemo(() => {
    if (selectedTaskId == null) return null
    return nodes.find(n => n.kind === 'task' && n.id === selectedTaskId)?.name || null
  }, [nodes, selectedTaskId])

  const doUpload = async () => {
    if (!selectedRun || !selectedArtifactId) return
    setSaving(true)
    setError(null)
    try {
      const created = await releaseService.create({
        train_run_id: selectedRun.id,
        train_artifact_id: selectedArtifactId,
      })
      await onCreated(created)
      onClose()
    } catch (e: any) {
      setError(String(e?.message || e))
      setConfirming(false)
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white w-[min(1280px,98vw)] h-[min(860px,92vh)] rounded-lg shadow-lg overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="font-semibold text-gray-900">업로드 실행</div>
          <XButton onClick={onClose} ariaLabel="닫기" disabled={saving} />
        </div>
        <div className="flex-1 min-h-0 grid grid-cols-12 gap-0">
          <div className="col-span-4 border-r min-h-0 overflow-auto">
            <div className="px-4 py-3 border-b text-sm font-semibold text-gray-900">프로젝트 / 작업 카테고리</div>
            <div className="p-2">
              {roots.map(r => renderTree(r))}
              {roots.length === 0 && <div className="text-sm text-gray-500 p-3">표시할 항목이 없습니다.</div>}
            </div>
          </div>
          <div className="col-span-8 min-h-0 overflow-auto">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-900">Run / 모델 아티팩트 선택</div>
              <div className="text-xs text-gray-500">{loadingRuns ? '로딩 중…' : (selectedTaskId ? `${runs.length}개` : '작업 카테고리를 선택하세요')}</div>
            </div>
            <div className="p-4 space-y-4">
              {error && <div className="px-3 py-2 rounded border border-red-200 bg-red-50 text-red-700 text-sm">{error}</div>}

              <div className="grid grid-cols-2 gap-3">
                <div className="border rounded overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 text-sm font-semibold text-gray-900">Runs</div>
                  <div className="max-h-[380px] overflow-auto divide-y">
                    {runs.map(r => (
                      <button
                        key={r.id}
                        className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${selectedRun?.id === r.id ? 'bg-gray-100' : ''}`}
                        onClick={async () => {
                          setError(null)
                          setSelectedArtifactId(null)
                          try { setSelectedRun(await trainService.getRun(r.id)) } catch { setSelectedRun(null) }
                        }}
                      >
                        <div className="text-sm font-medium text-gray-900">{`Run #${r.id}`}</div>
                        <div className="text-xs text-gray-500">{formatDT(r.created_at)} · {r.status}</div>
                      </button>
                    ))}
                    {runs.length === 0 && (
                      <div className="px-3 py-3 text-sm text-gray-500">
                        {selectedTaskId ? 'Run이 없습니다.' : '작업 카테고리를 선택하세요.'}
                      </div>
                    )}
                  </div>
                </div>

                <div className="border rounded overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 text-sm font-semibold text-gray-900">모델 아티팩트 (.pt/.onnx/.hef)</div>
                  <div className="max-h-[380px] overflow-auto divide-y">
                    {(filteredArtifacts || []).map(a => (
                      <label key={a.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="artifact"
                          checked={selectedArtifactId === a.id}
                          onChange={() => setSelectedArtifactId(a.id)}
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{a.file_name}</div>
                          <div className="text-xs text-gray-500">{(a.file_size || 0).toLocaleString()} bytes</div>
                        </div>
                      </label>
                    ))}
                    {selectedRun && filteredArtifacts.length === 0 && (
                      <div className="px-3 py-3 text-sm text-gray-500">
                        이 Run에는 .pt/.onnx/.hef 아티팩트가 없습니다.
                      </div>
                    )}
                    {!selectedRun && (
                      <div className="px-3 py-3 text-sm text-gray-500">
                        Run을 선택하세요.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                  onClick={onClose}
                  disabled={saving}
                >
                  취소
                </button>
                <button
                  className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                  disabled={saving || !selectedRun || !selectedArtifactId}
                  onClick={() => setConfirming(true)}
                >
                  업로드
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {confirming && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-white w-[min(480px,90vw)] rounded-lg shadow-lg overflow-hidden">
            <div className="px-4 py-3 border-b">
              <div className="font-semibold text-gray-900">업로드 확인</div>
            </div>
            <div className="p-4 space-y-3">
              <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm space-y-1">
                {selectedTaskName && (
                  <div className="flex gap-2">
                    <span className="text-gray-500 shrink-0">작업 카테고리</span>
                    <span className="text-gray-900 font-medium truncate">{selectedTaskName}</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <span className="text-gray-500 shrink-0">Run</span>
                  <span className="text-gray-900 font-medium">{selectedRun ? `Run #${selectedRun.id}` : '-'}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-500 shrink-0">파일</span>
                  <span className="text-gray-900 font-medium truncate">
                    {selectedArtifact ? `${selectedArtifact.file_name} (${(selectedArtifact.file_size || 0).toLocaleString()} bytes)` : '-'}
                  </span>
                </div>
              </div>
              <div className="text-sm text-red-700">
                업로드 후에는 다른 서버에서 관리되어 취소할 수 없습니다.
              </div>
              <div className="text-sm text-gray-900">정말 업로드하시겠습니까?</div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                  onClick={() => setConfirming(false)}
                  disabled={saving}
                >
                  취소
                </button>
                <button
                  className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                  onClick={doUpload}
                  disabled={saving}
                >
                  {saving ? '업로드 중…' : '업로드'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

